/**
 * Analytics — dashboard estilo Vercel.
 *
 * Trae visitas, pageviews, bounce rate, países, referrers, devices, browsers
 * desde el endpoint `/api/v1/analytics/overview` (que consulta Cloudflare
 * Analytics Engine via SQL API).
 *
 * Bloque "Uso del mes" se mantiene arriba (consumo del plan: API/orders/storage).
 *
 * Snippet de instalación: una card con el `<script>` que el tenant debe pegar
 * en su storefront — copia al portapapeles con un click.
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area,
} from 'recharts'
import {
  BarChart3, Users, Eye, TrendingDown, MousePointerClick, Globe, Monitor, Smartphone, Tablet,
  Copy, Check, Info, MapPin, Code,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, PageHeader, Badge, Button } from '@/components/ui'
import { adminApi, tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'

export const Route = createFileRoute('/store/$id/analytics')({
  component: StoreAnalyticsPage,
})

// ============================================================
// Tipos
// ============================================================

interface TenantUsage {
  period: string
  plan: string
  usage: {
    apiRequests: { used: number; max: number; pct: number }
    orders: { used: number; max: number; pct: number }
    bandwidthGB: { used: number; max: number; pct: number }
    storageGB: { used: number; max: number; pct: number }
  }
}

interface Overview {
  totals: {
    pageviews: number
    visitors: number
    sessions: number
    bounceRate: number
    avgPagesPerSession: number
  }
  timeseries: Array<{ date: string; pageviews: number; visitors: number }>
  topReferrers: Array<{ host: string; pageviews: number }>
  topPages: Array<{ path: string; pageviews: number }>
  countries: Array<{ country: string; pageviews: number }>
  devices: Array<{ device: string; pageviews: number }>
  browsers: Array<{ browser: string; pageviews: number }>
}

interface Snippet {
  snippet: string
  tenantId: string
  apiUrl: string
}

// ============================================================
// Helpers
// ============================================================

function formatNum(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return String(n)
}

function formatPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`
}

const COUNTRY_NAMES: Record<string, string> = {
  CO: 'Colombia', US: 'Estados Unidos', MX: 'México', AR: 'Argentina', PE: 'Perú',
  CL: 'Chile', EC: 'Ecuador', VE: 'Venezuela', BR: 'Brasil', ES: 'España',
  unknown: 'Desconocido',
}
const COUNTRY_FLAGS: Record<string, string> = {
  CO: '🇨🇴', US: '🇺🇸', MX: '🇲🇽', AR: '🇦🇷', PE: '🇵🇪', CL: '🇨🇱',
  EC: '🇪🇨', VE: '🇻🇪', BR: '🇧🇷', ES: '🇪🇸',
}

function countryName(code: string): string {
  return COUNTRY_NAMES[code] ?? code
}

function favicon(host: string): string {
  if (host === 'direct') return ''
  return `https://www.google.com/s2/favicons?domain=${host}&sz=32`
}

const REFERRER_LABELS: Record<string, string> = {
  'google.com': 'Google',
  'instagram.com': 'Instagram',
  'l.instagram.com': 'Instagram',
  'facebook.com': 'Facebook',
  'l.facebook.com': 'Facebook',
  'm.facebook.com': 'Facebook',
  'tiktok.com': 'TikTok',
  't.co': 'Twitter / X',
  'twitter.com': 'Twitter / X',
  'x.com': 'X (Twitter)',
  'youtube.com': 'YouTube',
  'reddit.com': 'Reddit',
  'pinterest.com': 'Pinterest',
  'linkedin.com': 'LinkedIn',
  'duckduckgo.com': 'DuckDuckGo',
  'bing.com': 'Bing',
  direct: 'Directo (sin referrer)',
}

function refLabel(host: string): string {
  return REFERRER_LABELS[host] ?? host
}

const DEVICE_ICONS: Record<string, React.FC<{ className?: string }>> = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
  bot: Code,
  unknown: BarChart3,
}

// ============================================================
// Página
// ============================================================

function StoreAnalyticsPage() {
  const { id } = Route.useParams()
  const { activeTenantId } = useAuth()
  const [days, setDays] = useState<7 | 30 | 90>(30)

  const usage = useQuery({
    queryKey: ['tenant-usage', id],
    queryFn: () => adminApi.get<{ data: TenantUsage }>(`/api/admin/tenants/${id}/usage`),
    refetchInterval: 30_000,
  })

  const overview = useQuery({
    queryKey: ['analytics-overview', activeTenantId, days],
    queryFn: () => tenantApi.get<{ data: Overview }>(`/api/v1/analytics/overview?days=${days}`),
    enabled: !!activeTenantId,
    refetchInterval: 60_000,
  })

  const snippet = useQuery({
    queryKey: ['analytics-snippet', activeTenantId],
    queryFn: () => tenantApi.get<{ data: Snippet }>(`/api/v1/analytics/snippet`),
    enabled: !!activeTenantId,
  })

  const ov = overview.data?.data
  const totals = ov?.totals
  const noData = totals && totals.pageviews === 0

  return (
    <>
      <PageHeader title="Analítica" description="Visitantes, sesiones y comportamiento del storefront" />
      <div className="p-8 space-y-6">
        {/* Uso del plan */}
        {usage.data && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Uso del mes ({usage.data.data.period})</CardTitle>
                <Badge variant="muted">plan {usage.data.data.plan}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <UsageBar label="API requests" {...usage.data.data.usage.apiRequests} unit="" />
              <UsageBar label="Pedidos" {...usage.data.data.usage.orders} unit="" />
              <UsageBar label="Bandwidth" {...usage.data.data.usage.bandwidthGB} unit="GB" />
              <UsageBar label="Storage" {...usage.data.data.usage.storageGB} unit="GB" />
            </CardContent>
          </Card>
        )}

        {/* Tab de rango */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Visitantes del storefront</h2>
          <div className="flex border border-neutral-800 rounded-lg overflow-hidden">
            {([7, 30, 90] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium transition-colors',
                  days === d ? 'bg-white text-neutral-900' : 'bg-neutral-900 text-neutral-400 hover:bg-neutral-800',
                )}
              >
                {d} días
              </button>
            ))}
          </div>
        </div>

        {/* Snippet de instalación si NO hay data — una vez activado se puede colapsar */}
        {snippet.data && (
          <SnippetCard data={snippet.data.data} dismissed={!noData} />
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Visitantes únicos" value={totals?.visitors ?? 0} icon={<Users className="size-4" />} loading={overview.isLoading} />
          <Kpi label="Pageviews" value={totals?.pageviews ?? 0} icon={<Eye className="size-4" />} loading={overview.isLoading} />
          <Kpi
            label="Bounce rate"
            value={formatPct(totals?.bounceRate ?? 0)}
            isString
            color={totals && totals.bounceRate > 0.7 ? 'text-red-400' : totals && totals.bounceRate > 0.5 ? 'text-yellow-400' : 'text-emerald-400'}
            icon={<TrendingDown className="size-4" />}
            loading={overview.isLoading}
          />
          <Kpi
            label="Páginas / sesión"
            value={(totals?.avgPagesPerSession ?? 0).toFixed(1)}
            isString
            icon={<MousePointerClick className="size-4" />}
            loading={overview.isLoading}
          />
        </div>

        {/* Timeseries */}
        <Card className="p-6">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="size-4 text-blue-400" />
            Visitas en el tiempo
          </h3>
          {overview.isLoading ? (
            <div className="h-64 bg-neutral-900 animate-pulse rounded" />
          ) : !ov?.timeseries.length ? (
            <div className="h-64 flex items-center justify-center text-sm text-neutral-500">
              Sin datos en el período seleccionado
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={ov.timeseries}>
                <defs>
                  <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(217, 91%, 60%)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(217, 91%, 60%)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="vGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="date" fontSize={11} stroke="#737373" tickFormatter={(v: string) => v.slice(5)} />
                <YAxis fontSize={11} stroke="#737373" />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #262626', backgroundColor: '#0a0a0a' }}
                  labelStyle={{ color: '#e5e5e5' }}
                />
                <Area type="monotone" dataKey="pageviews" stroke="hsl(217, 91%, 60%)" fill="url(#pvGrad)" strokeWidth={2} name="Pageviews" />
                <Area type="monotone" dataKey="visitors" stroke="hsl(142, 71%, 45%)" fill="url(#vGrad)" strokeWidth={2} name="Visitantes" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Grid: 2 col */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top referrers */}
          <BreakdownCard
            title="¿De dónde llegan?"
            icon={<Globe className="size-4 text-purple-400" />}
            items={ov?.topReferrers ?? []}
            getKey={(r) => r.host}
            getValue={(r) => r.pageviews}
            getLabel={(r) => refLabel(r.host)}
            getIcon={(r) => r.host !== 'direct'
              ? <img src={favicon(r.host)} alt="" className="size-4 rounded" loading="lazy" />
              : <Globe className="size-4 text-neutral-500" />}
            getSubLabel={(r) => r.host !== 'direct' ? r.host : null}
            loading={overview.isLoading}
          />

          {/* Top pages */}
          <BreakdownCard
            title="Páginas más vistas"
            icon={<Eye className="size-4 text-blue-400" />}
            items={ov?.topPages ?? []}
            getKey={(p) => p.path}
            getValue={(p) => p.pageviews}
            getLabel={(p) => p.path}
            getIcon={() => <Eye className="size-4 text-neutral-500" />}
            loading={overview.isLoading}
            mono
          />

          {/* Countries */}
          <BreakdownCard
            title="Países"
            icon={<MapPin className="size-4 text-emerald-400" />}
            items={ov?.countries ?? []}
            getKey={(c) => c.country}
            getValue={(c) => c.pageviews}
            getLabel={(c) => countryName(c.country)}
            getIcon={(c) => <span className="text-base leading-none">{COUNTRY_FLAGS[c.country] ?? '🌐'}</span>}
            getSubLabel={(c) => c.country !== 'unknown' ? c.country : null}
            loading={overview.isLoading}
          />

          {/* Devices + browsers combined */}
          <Card className="p-6">
            <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <Monitor className="size-4 text-orange-400" />
              Dispositivos y navegadores
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Dispositivos</p>
                {(ov?.devices ?? []).slice(0, 5).map((d) => {
                  const Icon = DEVICE_ICONS[d.device] ?? BarChart3
                  const totalPv = totals?.pageviews ?? 1
                  const pct = (d.pageviews / totalPv) * 100
                  return (
                    <div key={d.device} className="flex items-center gap-2 text-sm py-1">
                      <Icon className="size-4 text-neutral-400 shrink-0" />
                      <span className="capitalize flex-1">{d.device}</span>
                      <span className="text-xs text-neutral-500 tabular-nums">{pct.toFixed(0)}%</span>
                    </div>
                  )
                })}
                {!overview.isLoading && (ov?.devices.length ?? 0) === 0 && <p className="text-xs text-neutral-500">—</p>}
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Navegadores</p>
                {(ov?.browsers ?? []).slice(0, 5).map((b) => {
                  const totalPv = totals?.pageviews ?? 1
                  const pct = (b.pageviews / totalPv) * 100
                  return (
                    <div key={b.browser} className="flex items-center gap-2 text-sm py-1">
                      <span className="capitalize flex-1">{b.browser}</span>
                      <span className="text-xs text-neutral-500 tabular-nums">{pct.toFixed(0)}%</span>
                    </div>
                  )
                })}
                {!overview.isLoading && (ov?.browsers.length ?? 0) === 0 && <p className="text-xs text-neutral-500">—</p>}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  )
}

// ============================================================
// SnippetCard
// ============================================================

function SnippetCard({ data, dismissed }: { data: Snippet; dismissed: boolean }) {
  const [copied, setCopied] = useState(false)
  const [open, setOpen] = useState(!dismissed)

  function handleCopy() {
    navigator.clipboard.writeText(data.snippet)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Card className={cn('p-5', !dismissed && 'border-blue-800 bg-blue-950/20')}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <Info className="size-4 text-blue-400 shrink-0" />
          <div>
            <p className="font-semibold text-sm">
              {dismissed ? 'Snippet de instalación' : 'Activa el tracking en tu storefront'}
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">
              {dismissed
                ? 'Pegá este script en cualquier storefront del tenant para registrar pageviews.'
                : 'Aún no recibimos pageviews. Pegá este script en el `<head>` de tu storefront.'}
            </p>
          </div>
        </div>
        <span className="text-xs text-neutral-500">{open ? 'Ocultar' : 'Mostrar'}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <pre className="text-xs bg-neutral-950 border border-neutral-800 rounded p-3 overflow-x-auto select-all">
            <code>{data.snippet}</code>
          </pre>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleCopy}>
              {copied ? <><Check className="size-4 mr-1.5" /> Copiado</> : <><Copy className="size-4 mr-1.5" /> Copiar snippet</>}
            </Button>
            <p className="text-xs text-neutral-500 self-center">
              Tracking en background, &lt;2KB, sin cookies. Compatible con cualquier framework.
            </p>
          </div>
        </div>
      )}
    </Card>
  )
}

// ============================================================
// Subcomponentes
// ============================================================

function Kpi({ label, value, color, icon, isString, loading }: {
  label: string
  value: number | string
  color?: string
  icon?: React.ReactNode
  isString?: boolean
  loading?: boolean
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-xs text-neutral-400 uppercase tracking-wider">
        {icon}
        <span>{label}</span>
      </div>
      {loading ? (
        <div className="h-8 mt-2 w-20 bg-neutral-800 animate-pulse rounded" />
      ) : (
        <p className={cn('text-3xl font-bold tabular-nums mt-2', color)}>
          {isString ? value : formatNum(Number(value))}
        </p>
      )}
    </Card>
  )
}

function BreakdownCard<T>({
  title, icon, items, getKey, getValue, getLabel, getIcon, getSubLabel, loading, mono,
}: {
  title: string
  icon: React.ReactNode
  items: T[]
  getKey: (item: T) => string
  getValue: (item: T) => number
  getLabel: (item: T) => string
  getIcon?: (item: T) => React.ReactNode
  getSubLabel?: (item: T) => string | null
  loading?: boolean
  mono?: boolean
}) {
  const total = items.reduce((s, i) => s + getValue(i), 0)
  return (
    <Card className="p-6">
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
        {icon}
        {title}
      </h3>
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-8 bg-neutral-900 animate-pulse rounded" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-neutral-500 py-4 text-center">Sin datos aún</p>
      ) : (
        <div className="space-y-1.5">
          {items.slice(0, 8).map((item) => {
            const value = getValue(item)
            const pct = total > 0 ? (value / total) * 100 : 0
            const subLabel = getSubLabel?.(item)
            return (
              <div key={getKey(item)} className="relative">
                <div className="flex items-center gap-2 text-sm py-1.5 relative z-10">
                  {getIcon?.(item)}
                  <div className="flex-1 min-w-0">
                    <span className={cn('truncate block', mono && 'font-mono text-xs')}>{getLabel(item)}</span>
                    {subLabel && <span className="text-[10px] text-neutral-500">{subLabel}</span>}
                  </div>
                  <span className="text-xs font-medium tabular-nums">{formatNum(value)}</span>
                  <span className="text-[10px] text-neutral-500 tabular-nums w-10 text-right">{pct.toFixed(1)}%</span>
                </div>
                {/* Barra de fondo proporcional */}
                <div
                  className="absolute left-0 top-0 bottom-0 bg-neutral-900 rounded transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

function UsageBar({ label, used, max, pct, unit }: { label: string; used: number; max: number; pct: number; unit: string }) {
  const danger = pct >= 90
  const warn = pct >= 70 && pct < 90
  const color = danger ? 'bg-red-500' : warn ? 'bg-yellow-500' : 'bg-green-500'
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-neutral-300">{label}</span>
        <span className="font-mono text-neutral-400">
          {used.toLocaleString()}{unit} / {max.toLocaleString()}{unit} <span className="text-neutral-500">({pct}%)</span>
        </span>
      </div>
      <div className="h-2 bg-neutral-800 rounded overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  )
}
